import { useEffect } from 'react';
import clsx from 'clsx';

interface Props {
  ariaLabel: string;
  onClose: () => void;
  maxWidth?: number;
  maxHeight?: string;
  className?: string;
  children: React.ReactNode;
}

// Shared shell for keyboard-summoned centered pop-ups (QuickAdd, HotkeysHelp,
// Navigator). Provides the backdrop, escape-to-close, click-outside-to-close,
// and the bordered card chrome — everything that should look identical across
// them.
export default function CenteredOverlay({
  ariaLabel,
  onClose,
  maxWidth = 520,
  maxHeight,
  className,
  children
}: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className={clsx(
          'flex w-full flex-col overflow-hidden rounded-lg border border-[var(--border-strong)] bg-[var(--popover-bg)] shadow-[var(--shadow-md)]',
          className
        )}
        style={{ maxWidth, ...(maxHeight ? { maxHeight } : {}) }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>
  );
}
